import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./transport.css";

export default function BusDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const school   = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [bus,             setBus]             = useState(null);
  const [students,        setStudents]        = useState([]);
  const [selectedSeat,    setSelectedSeat]    = useState(null);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [allocating,      setAllocating]      = useState(false);

  const loadBus = async () => {
    try {
      const res = await API.get(`/school-admin/buses/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setBus(res.data);
    } catch (err) { console.error(err); }
  };

  const loadStudents = async () => {
    try {
      const res = await API.get("/school-admin/transport/available-students/", {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setStudents(res.data.students || []);
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    loadBus();
    loadStudents();
  }, [id]);

  const allocateSeat = async () => {
    if (!selectedStudent) return alert("Please select a student");
    try {
      setAllocating(true);
      await API.post(
        "/school-admin/buses/allocate/",
        { bus_id: id, seat_id: selectedSeat.seat_id, student_id: selectedStudent },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setSelectedSeat(null);
      setSelectedStudent("");
      loadBus();
    } catch (err) { console.error(err); alert("Failed to allocate seat"); }
    finally { setAllocating(false); }
  };

  const removeSeat = async (e, seatId) => {
    e.stopPropagation();
    if (!window.confirm("Remove this student from the seat?")) return;
    try {
      await API.post(
        "/school-admin/buses/remove-seat/",
        { seat_id: seatId },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      loadBus();
    } catch (err) { console.error(err); alert("Failed to remove student"); }
  };

  if (!bus) return (
    <div className="tr-page">
      <div className="tr-empty"><div className="tr-empty-icon">⏳</div><strong>Loading bus…</strong></div>
    </div>
  );

  const occupied  = bus.seats.filter(s =>  s.is_occupied).length;
  const available = bus.seats.filter(s => !s.is_occupied).length;

  return (
    <div className="tr-page">

      {/* ══ HERO ══ */}
      <div className="tr-hero">
        <div className="tr-hero-left">
          <div className="tr-hero-icon">🚍</div>
          <div>
            <h2 className="tr-hero-title">{bus.bus_number}</h2>
            <p className="tr-hero-sub">{bus.route_name} &nbsp;·&nbsp; {occupied}/{bus.total_seats} seats occupied</p>
          </div>
        </div>
        <div className="tr-hero-right">
          <button className="tr-btn-back" onClick={() => navigate("/school-admin/transport/buses")}>← Buses</button>
          <button className="tr-btn-add"  onClick={() => navigate(`/school-admin/transport/buses/edit/${id}`)}>✏️ Edit</button>
        </div>
      </div>

      {/* ══ ROUTE PILL ══ */}
      <div className="tr-route-pill">
        📍 {bus.start_point} &nbsp;→&nbsp; {bus.end_point}
      </div>

      {/* ══ INFO STRIP ══ */}
      <div className="tr-info-strip">
        <div className="tr-info-card">
          <span className="tr-ic-label">Departure</span>
          <span className="tr-ic-value">{bus.start_time || "—"}</span>
        </div>
        <div className="tr-info-card">
          <span className="tr-ic-label">Arrival</span>
          <span className="tr-ic-value">{bus.arrival_time || "—"}</span>
        </div>
        <div className="tr-info-card">
          <span className="tr-ic-label">Driver</span>
          <span className="tr-ic-value">{bus.driver?.name || "—"}</span>
          {bus.driver?.phone && <span className="tr-ic-sub">{bus.driver.phone}</span>}
        </div>
        <div className="tr-info-card">
          <span className="tr-ic-label">Cleaner</span>
          <span className="tr-ic-value">{bus.cleaner?.name || "—"}</span>
          {bus.cleaner?.phone && <span className="tr-ic-sub">{bus.cleaner.phone}</span>}
        </div>
      </div>

      {/* ══ SEAT STATS ══ */}
      <div className="tr-seat-stats">
        <div className="tr-stat-card total">
          <div className="tr-stat-num">{bus.total_seats}</div>
          <div className="tr-stat-lbl">Total Seats</div>
        </div>
        <div className="tr-stat-card occupied">
          <div className="tr-stat-num">{occupied}</div>
          <div className="tr-stat-lbl">Occupied</div>
        </div>
        <div className="tr-stat-card free">
          <div className="tr-stat-num">{available}</div>
          <div className="tr-stat-lbl">Available</div>
        </div>
      </div>

      {/* ══ SEAT GRID ══ */}
      <div className="tr-seat-grid-wrap">
        <p className="tr-seat-grid-title">🪑 Seat Layout — click an available seat to assign a student</p>
        <div className="tr-seat-grid">
          {bus.seats.map(s => (
            <div
              key={s.seat_id}
              className={`tr-seat ${s.is_occupied ? "occupied" : "available"}`}
              onClick={() => !s.is_occupied && setSelectedSeat(s)}
            >
              <div className="tr-seat-num">{s.seat_number}</div>
              {s.is_occupied ? (
                <>
                  <div className="tr-seat-student">{s.student}</div>
                  <button className="tr-seat-remove" onClick={e => removeSeat(e, s.seat_id)}>
                    ✕ Remove
                  </button>
                </>
              ) : (
                <div className="tr-seat-avail">Available</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ══ ALLOCATION MODAL ══ */}
      {selectedSeat && (
        <div className="tr-modal-overlay" onClick={() => setSelectedSeat(null)}>
          <div className="tr-modal" onClick={e => e.stopPropagation()}>
            <div className="tr-modal-head">
              <div className="tr-modal-head-icon">🪑</div>
              <div>
                <h3>Assign Seat {selectedSeat.seat_number}</h3>
                <p>Select a student to occupy this seat</p>
              </div>
            </div>
            <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)}>
              <option value="">— Select Student —</option>
              {students.map(st => (
                <option key={st.id} value={st.id}>{st.name}</option>
              ))}
            </select>
            <div className="tr-modal-actions">
              <button className="tr-modal-cancel"   onClick={() => setSelectedSeat(null)}>Cancel</button>
              <button className="tr-modal-allocate" onClick={allocateSeat} disabled={allocating}>
                {allocating ? <><div className="tr-spinner"></div> Assigning…</> : "✓ Assign Seat"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
